// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Team member management only
// 2. Separation of Concerns - UI separated from API calls
// 3. Modular Architecture - Reusable member management component
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Close as CloseIcon
} from '@mui/icons-material';

import teamManagementService from '../../services/teamManagementService';
import userManagementService from '../../services/userManagementService';
import ConfirmDialog from '../common/ConfirmDialog';
import AlertDialog from '../common/AlertDialog';

const TeamMemberManagement = ({ open, team, onClose, onSave }) => {
  const [members, setMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [memberRole, setMemberRole] = useState('member');
  const [canEditQA, setCanEditQA] = useState(true);
  const [canViewAllFiles, setCanViewAllFiles] = useState(true);
  
  const [confirmDialog, setConfirmDialog] = useState({ open: false, member: null });
  const [alertDialog, setAlertDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'info'
  });

  useEffect(() => {
    if (open && team) {
      loadData();
    }
  }, [open, team]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load team members
      const membersData = await teamManagementService.listMembers(team.id);
      setMembers(membersData);
      
      // Load all active users for adding
      const usersData = await userManagementService.user.list({ is_active: true });
      setAvailableUsers(usersData.users || []);
    } catch (err) {
      console.error('Failed to load member data:', err);
      setError('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    setShowAddMember(true);
    setSelectedUserIds([]);
    setMemberRole('member');
    setCanEditQA(true);
    setCanViewAllFiles(true);
  };

  const handleCancelAdd = () => {
    setShowAddMember(false);
    setSelectedUserIds([]);
  };

  const handleConfirmAdd = async () => {
    if (selectedUserIds.length === 0) {
      setAlertDialog({
        open: true,
        title: 'Warning',
        message: 'Please select at least one user to add',
        severity: 'warning'
      });
      return;
    }
    
    try {
      await teamManagementService.addMembers(team.id, {
        user_ids: selectedUserIds,
        role: memberRole,
        can_edit_qa: canEditQA,
        can_view_all_files: canViewAllFiles
      });
      
      setShowAddMember(false);
      loadData();
      
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Added ${selectedUserIds.length} member(s) successfully!`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to add members:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to add members',
        severity: 'error'
      });
    }
  };

  const handleRemoveMember = (member) => {
    setConfirmDialog({ open: true, member });
  };

  const handleConfirmRemove = async () => {
    const { member } = confirmDialog;
    try {
      await teamManagementService.removeMember(team.id, member.user_id);
      setConfirmDialog({ open: false, member: null });
      loadData();
      
      setAlertDialog({
        open: true,
        title: 'Success',
        message: `Removed ${member.username} from team`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Failed to remove member:', err);
      setAlertDialog({
        open: true,
        title: 'Error',
        message: err.response?.data?.detail || 'Failed to remove member',
        severity: 'error'
      });
    }
  };

  const handleCancelRemove = () => {
    setConfirmDialog({ open: false, member: null });
  };

  // Filter available users (exclude current members)
  const memberUserIds = members.map(m => m.user_id);
  const filteredAvailableUsers = availableUsers.filter(
    u => !memberUserIds.includes(u.id)
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">Manage Team Members</Typography>
            <Typography variant="body2" color="text.secondary">
              {team.name}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Add Member Section */}
            {!showAddMember ? (
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleAddMember}
                  fullWidth
                >
                  Add Team Members
                </Button>
              </Box>
            ) : (
              <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add New Members
                </Typography>
                
                <FormControl fullWidth margin="normal" size="small">
                  <InputLabel>Select Users *</InputLabel>
                  <Select
                    multiple
                    value={selectedUserIds}
                    onChange={(e) => setSelectedUserIds(e.target.value)}
                    label="Select Users *"
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {selected.map((userId) => {
                          const user = availableUsers.find(u => u.id === userId);
                          return (
                            <Chip
                              key={userId}
                              label={user?.username || userId}
                              size="small"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {filteredAvailableUsers.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.full_name || user.username} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                
                
                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={canEditQA}
                        onChange={(e) => setCanEditQA(e.target.checked)}
                      />
                    }
                    label="Can Edit QA Criteria"
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={canViewAllFiles}
                        onChange={(e) => setCanViewAllFiles(e.target.checked)}
                      />
                    }
                    label="Can View All Voice Files"
                  />
                </Box>
                
                <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                  <Button
                    variant="contained"
                    onClick={handleConfirmAdd}
                    size="small"
                    disabled={selectedUserIds.length === 0}
                  >
                    Add Members
                  </Button>
                  <Button onClick={handleCancelAdd} size="small">
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
            
            <Divider sx={{ my: 2 }} />
            
            {/* Current Members List */}
            <Typography variant="subtitle2" gutterBottom>
              Current Members ({members.length})
            </Typography>
            
            {members.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No members in this team yet
                </Typography>
              </Box>
            ) : (
              <List>
                {members.map((member) => (
                  <ListItem
                    key={member.id}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {member.full_name || member.username}
                          </Typography>
                          <Chip label={member.role} size="small" color="primary" />
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            @{member.username}
                          </Typography>
                          {' • '}
                          <Chip
                            label={member.can_edit_qa ? 'Can Edit QA' : 'View QA Only'}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '18px' }}
                          />
                          {' '}
                          <Chip
                            label={member.can_view_all_files ? 'All Files' : 'Own Files'}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem', height: '18px' }}
                          />
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        size="small"
                        color="error"
                        onClick={() => handleRemoveMember(member)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
      
      {/* Confirm Remove Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={handleCancelRemove}
        onConfirm={handleConfirmRemove}
        title="Remove Team Member"
        message={`Are you sure you want to remove ${confirmDialog.member?.username} from this team?`}
        confirmText="Remove"
        cancelText="Cancel"
        severity="warning"
        confirmColor="error"
      />
      
      {/* Alert Dialog */}
      <AlertDialog
        open={alertDialog.open}
        onClose={() => setAlertDialog({ ...alertDialog, open: false })}
        title={alertDialog.title}
        message={alertDialog.message}
        severity={alertDialog.severity}
      />
    </Dialog>
  );
};

export default TeamMemberManagement;
